import {
  Arrange,
  Button,
  Container,
  Split,
  SplitSection,
  SvgAdd,
  SvgRedo,
  SvgSplit,
  SvgTrash,
  SvgUndo,
  Text,
  Tooltip,
} from '@loomhq/lens';
import React from 'react';
import { useDispatch } from 'react-redux';

import { TRIM_SPLIT_PLAY_RANGE } from 'constants/hotkeys';
import { isMac } from 'utilities/device';
import { secondsToVideoTS } from 'utilities/time';
import { ROLLOUT_WEAVE, VARIANT } from 'constants/featureFlag';
import FeatureFlaggedComponent from 'components/feature-flag-wrapper';

import styles from './styles.module.less';
import { openModal } from 'actions/modals';
import { ADD_LOOM_SEARCH_MODAL } from 'constants/modalTypes';

const SmallButton = props => <Button size="small" {...props} />;

interface Props {
  currentTime: number;
  canSplitPlayRange: boolean;
  splitPlayRange: () => void;
  canDeletePlayRange: boolean;
  deletePlayRange: () => void;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  revertToOriginal: () => void;
  canRevertToOriginal: boolean;
  videoId: string;
}

interface TooltipWithShortcutOptions {
  content: string;
  shortcut: string;
  children: React.ReactNode;
  shortCutMinWidth?: string;
}

const TooltipWithShortcut = ({
  content,
  shortcut,
  children,
  shortCutMinWidth = undefined,
}: TooltipWithShortcutOptions) => (
  <Tooltip
    placement="bottomCenter"
    maxWidth="40"
    keepOpen={true}
    content={
      <Arrange gap="12px">
        <Text size="small" fontWeight="medium">
          {content}
        </Text>
        <Container
          paddingX="xsmall"
          paddingY="0"
          minWidth={shortCutMinWidth}
          backgroundColor="rgba(255, 255, 255, 0.15)"
          style={{ borderRadius: '3px' }}
        >
          <Text
            size="small"
            color="rgba(244, 244, 250, 0.6)"
            fontWeight="medium"
          >
            {shortcut}
          </Text>
        </Container>
      </Arrange>
    }
  >
    {children}
  </Tooltip>
);

const TrimControlBar = ({
  currentTime,
  canSplitPlayRange,
  splitPlayRange,
  canDeletePlayRange,
  deletePlayRange,
  canUndo,
  canRedo,
  undo,
  redo,
  revertToOriginal,
  canRevertToOriginal,
  videoId,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  return (
    <Split gap="medium" justifyContent="space-between">
      <SplitSection>
        <Arrange gap="small">
          <TooltipWithShortcut
            content="Split to trim in the middle of a clip"
            shortcut={TRIM_SPLIT_PLAY_RANGE[0]}
          >
            <SmallButton
              isDisabled={!canSplitPlayRange}
              icon={<SvgSplit />}
              onClick={splitPlayRange}
            >
              Split{' '}
              {!isNaN(currentTime) && (
                <>
                  at{' '}
                  <span className={styles.timeDisplay}>
                    {secondsToVideoTS(Math.round(currentTime))}
                  </span>{' '}
                </>
              )}
            </SmallButton>
          </TooltipWithShortcut>
          <TooltipWithShortcut
            content="Remove the selected clip"
            shortcut="Del"
          >
            <SmallButton
              isDisabled={!canDeletePlayRange}
              icon={<SvgTrash />}
              onClick={deletePlayRange}
            >
              Delete
            </SmallButton>
          </TooltipWithShortcut>
        </Arrange>
      </SplitSection>
      <SplitSection>
        <FeatureFlaggedComponent
          flag={ROLLOUT_WEAVE}
          activationValues={VARIANT}
        >
          <Tooltip content="Start building a multi part loom">
            <SmallButton
              isDisabled={false}
              icon={<SvgAdd />}
              onClick={() =>
                dispatch(openModal(ADD_LOOM_SEARCH_MODAL, { videoId }))
              }
            >
              Add clip
            </SmallButton>
          </Tooltip>
        </FeatureFlaggedComponent>
      </SplitSection>
      <SplitSection>
        <Arrange gap="small">
          <SmallButton
            isDisabled={!canRevertToOriginal}
            onClick={revertToOriginal}
          >
            Revert to original
          </SmallButton>
          <TooltipWithShortcut
            content="Undo"
            shortcut={isMac ? 'Cmd-Z' : 'Ctrl-Z'}
          >
            <SmallButton
              isDisabled={!canUndo}
              icon={<SvgUndo />}
              onClick={undo}
            >
              Undo
            </SmallButton>
          </TooltipWithShortcut>
          <TooltipWithShortcut
            content="Redo"
            shortcut={isMac ? 'Cmd-Shift-Z' : 'Ctrl-Shift-Z'}
            shortCutMinWidth={isMac ? '78px' : '72px'} // added to deal with width edge interaction
          >
            <SmallButton
              isDisabled={!canRedo}
              icon={<SvgRedo />}
              onClick={redo}
            >
              Redo
            </SmallButton>
          </TooltipWithShortcut>
        </Arrange>
      </SplitSection>
    </Split>
  );
};

export default TrimControlBar;
